import React, { useEffect, useState, useRef, useLayoutEffect, lazy, Component } from 'react';
import ReactDOM from 'react-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { Link, useNavigate } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { ArrowLeft, ArrowRight, ArrowDown, Image, CheckCircle, X, MagnifyingGlassPlus, CaretLeft, CaretRight, TextT, Layout, Compass, Database, Lightning, Target, Rocket, WarningCircle, Users, Eye, FileText, MagnifyingGlass, Lightbulb, Clock, UserCheck, TrendUp, Gear, BookOpen, ChatCircle, NavigationArrow, Stack, PencilSimple, Info } from '@phosphor-icons/react';
import { useScrollAnimation } from '../utils/AnimationObserver';
import '../styles/animations.css';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
// Optimized Image Component
function SimpleImage({
  src,
  alt,
  className = ''
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  return <div className={`overflow-hidden rounded-lg relative ${className}`} style={{
    contain: 'content'
  }} data-id="element-782">
      {!isLoaded && <div className="w-full h-full bg-gray-100 absolute inset-0 animate-pulse" data-id="element-783" />}
      <img src={src} alt={alt} loading="lazy" decoding="async" onLoad={() => setIsLoaded(true)} className={`w-full h-auto transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} data-id="element-784" />
    </div>;
}
// Modal Portal Component
function ModalPortal({
  children,
  isOpen
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  if (!mounted || !isOpen) return null;
  return ReactDOM.createPortal(children, document.body);
}
// Lightbox Image Component
function LightboxImage({
  src,
  alt,
  label
}: {
  src: string;
  alt: string;
  label?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <>
      <div className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-50 border border-gray-100" onClick={() => setIsOpen(true)} data-id="element-785">
        <img src={src} alt={alt} className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-786" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center" data-id="element-787">
          <div className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300" data-id="element-788">
            <div className="bg-white text-[#003071] px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2" data-id="element-789">
              <MagnifyingGlassPlus className="w-4 h-4" data-id="element-790" />
              Zoom
            </div>
          </div>
        </div>
      </div>

      <ModalPortal isOpen={isOpen} data-id="element-791">
        <div onClick={() => setIsOpen(false)} className="fixed inset-0 bg-white/95 z-[99999] flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm" data-id="element-792">
          <button onClick={e => {
          e.stopPropagation();
          setIsOpen(false);
        }} className="absolute top-6 right-6 p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-793">
            <X className="w-6 h-6 text-black" data-id="element-794" />
          </button>

          {label && <div className="absolute top-8 left-8 text-black text-xl font-medium" data-id="element-795">
              {label}
            </div>}

          <img src={src} alt={alt} onClick={e => e.stopPropagation()} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" data-id="element-796" />
        </div>
      </ModalPortal>
    </>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    src: string;
    alt: string;
    label: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  };
  return <div className="relative w-full bg-gray-100 rounded-2xl overflow-hidden shadow-sm border border-gray-200" data-id="element-797">
      <div className="aspect-[16/9] relative" data-id="element-798">
        <img src={images[currentIndex].src} alt={images[currentIndex].alt} className="w-full h-full object-contain bg-gray-50" data-id="element-799" />
        <button onClick={prevSlide} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full shadow-lg transition-all backdrop-blur-sm" aria-label="Previous image" data-id="element-800">
          <CaretLeft className="w-4 h-4 sm:w-6 sm:h-6" data-id="element-801" />
        </button>
        <button onClick={nextSlide} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full shadow-lg transition-all backdrop-blur-sm" aria-label="Next image" data-id="element-802">
          <CaretRight className="w-4 h-4 sm:w-6 sm:h-6" data-id="element-803" />
        </button>
        <div className="absolute bottom-2 sm:bottom-6 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-md text-white px-3 py-1.5 sm:px-6 sm:py-2 rounded-full text-xs sm:text-sm font-medium max-w-[90%] text-center" data-id="element-804">
          {images[currentIndex].label}
        </div>
      </div>
      <div className="absolute bottom-2 sm:bottom-4 left-0 right-0 flex justify-center gap-1.5 sm:gap-2" data-id="element-805">
        {images.map((_, idx) => <button key={idx} onClick={() => setCurrentIndex(idx)} className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-4 sm:w-6' : 'bg-white/50'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-806" />)}
      </div>
    </div>;
}
// Vertical Timeline Component
function VerticalTimeline() {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  const steps = [{
    title: 'User-Centric Validation',
    desc: 'Recurring sessions with faculty and staff to ensure every decision was validated through direct user input'
  }, {
    title: 'Rapid Iteration',
    desc: 'Leveraged AI tools during wireframing to accelerate concept generation and testing'
  }, {
    title: 'Parallel Innovation',
    desc: 'Improved core UX while developing AI-powered CV parsing with Python engineers'
  }, {
    title: 'Continuous Research',
    desc: 'Video documented feedback, used Dovetail AI for pattern recognition, synthesized through affinity diagramming'
  }, {
    title: 'Live Refinement',
    desc: 'Conducted sessions with engineers and stakeholders to transform technical constraints into design opportunities'
  }, {
    title: 'Strategic Partnerships',
    desc: 'Demonstrated working prototypes to secure Amazon partnership for pilot testing'
  }];
  return <div ref={containerRef} className="relative py-12 sm:py-20 ml-4 sm:ml-8 md:ml-12" data-id="element-807">
      {/* Vertical Line Background */}
      <div className="absolute left-[19px] sm:left-[27px] top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-808" />

      {/* Animated Vertical Line */}
      <motion.div className="absolute left-[19px] sm:left-[27px] top-0 w-0.5 bg-[#003071] origin-top" style={{
      scaleY,
      height: '100%'
    }} data-id="element-809" />

      <div className="space-y-16 sm:space-y-24" data-id="element-810">
        {steps.map((step, idx) => <TimelineStep key={idx} step={step} index={idx} data-id="element-811" />)}
      </div>
    </div>;
}
function TimelineStep({
  step,
  index
}: {
  step: {
    title: string;
    desc: string;
  };
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="relative flex items-start gap-8 sm:gap-12" data-id="element-812">
      {/* Number Circle */}
      <div className="relative z-10 flex-shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white border-2 border-[#003071] flex items-center justify-center text-[#003071] font-bold text-sm sm:text-lg shadow-sm" data-id="element-813">
        {index + 1}
      </div>

      {/* Content Card */}
      <div className="flex-1 pt-1 sm:pt-2" data-id="element-814">
        <h3 className="text-lg sm:text-xl font-medium text-[#003071] mb-2 sm:mb-3" data-id="element-815">
          {step.title}
        </h3>
        <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl" data-id="element-816">
          {step.desc}
        </p>
      </div>
    </motion.div>;
}
interface CaseStudyCaseWesternProps {
  isOverlay?: boolean;
}
export function CaseStudyCaseWestern({
  isOverlay = false
}: CaseStudyCaseWesternProps) {
  const {
    viewPreference,
    isChatDrawerOpen,
    navigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  const {
    openChatDrawer,
    previousPage,
    setPreviousPage
  } = usePortfolio();
  // Handle back navigation to home
  const handleBackToHome = () => {
    // Clear navigation flags
    sessionStorage.removeItem('cameFromChat');
    sessionStorage.removeItem('navigatedFromHome');
    // Navigate first
    navigate('/');
    // FIXED: Scroll to top AFTER navigation completes
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  const [activeSection, setActiveSection] = useState('overview');
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'objectives', 'discovery', 'process', 'results'];
      const scrollPosition = window.scrollY + 200;
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };
  const handleCaseStudyClick = (slug: string) => {
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  const navItems = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'objectives',
    label: 'Objectives'
  }, {
    id: 'discovery',
    label: 'Problem'
  }, {
    id: 'process',
    label: 'Solution'
  }, {
    id: 'results',
    label: 'Impact'
  }];
  return <>
      {!isOverlay && <CustomCursor data-id="element-817" />}

      <div className="flex min-h-screen bg-white text-black font-switzer" data-id="element-818">
        <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-819">
          {!isOverlay && <CaseStudyHeader data-id="element-820" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-821" />}

          {/* Hero Section - Clean White Background */}
          <section className="pt-24 sm:pt-32 pb-8 sm:pb-12 px-4 sm:px-8 md:px-12 lg:px-24 max-w-[1400px] mx-auto w-full" data-id="element-822">
            <div className="max-w-4xl" data-id="element-823">
              {/* Back to Portfolio button - always visible */}
              {!isOverlay && <button onClick={handleBackToHome} className="flex items-center gap-2 text-gray-600 hover:text-[#003071] transition-colors mb-6 sm:mb-8 group" aria-label="Back to portfolio" data-id="element-824">
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform" data-id="element-825" />
                  <span className="text-sm font-medium" data-id="element-826">Back to Portfolio</span>
                </button>}

              <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-6 sm:mb-8 text-[#003071] leading-[1.1]" data-id="element-827">
                Case Western Reserve University
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl font-light" data-id="element-828">
                Redesigning a Faculty Profile & Activity Tracking System:
                Bridging Technical Complexity and Human-Centered Design
              </p>
            </div>
          </section>

          {/* Full Width Hero Image */}
          <div className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[80vh] overflow-hidden mb-16 sm:mb-24" data-id="element-829">
            <img src="https://cdn.magicpatterns.com/uploads/61dpXs15GKMAKBJcYfBiri/CWRU_COMPV.png" alt="Case Western Hero" className="w-full h-full object-cover object-center" data-id="element-830" />
          </div>

          {/* Main Content Grid */}
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-8 md:px-12 lg:px-24 pb-32" data-id="element-831">
            <div className="lg:grid lg:grid-cols-12 gap-12" data-id="element-832">
              {/* Sticky Navigation Sidebar */}
              <div className="hidden lg:block lg:col-span-3" data-id="element-833">
                <div className="sticky top-32 space-y-6" data-id="element-834">
                  {navItems.map(item => <button key={item.id} onClick={() => scrollToSection(item.id)} className={`block text-sm tracking-wide transition-colors duration-300 ${activeSection === item.id ? 'text-[#003071] font-medium border-l-2 border-[#003071] pl-4' : 'text-gray-400 hover:text-gray-600 pl-4 border-l-2 border-transparent'}`} data-id="element-835">
                      {item.label}
                    </button>)}
                </div>
              </div>

              {/* Content Column */}
              <div className="lg:col-span-9 space-y-32 sm:space-y-40" data-id="element-836">
                {/* Overview Section */}
                <section id="overview" className="scroll-mt-32" data-id="element-837">
                  <div className="grid md:grid-cols-3 gap-16 mb-16" data-id="element-838">
                    <div data-id="element-839">
                      <h3 className="text-sm font-medium text-[#003071] uppercase tracking-wider mb-2" data-id="element-840">
                        Role
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-841">
                        Lead Product Designer
                      </p>
                    </div>
                    <div data-id="element-842">
                      <h3 className="text-sm font-medium text-[#003071] uppercase tracking-wider mb-2" data-id="element-843">
                        Timeline
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-844">6 Months</p>
                    </div>
                    <div data-id="element-845">
                      <h3 className="text-sm font-medium text-[#003071] uppercase tracking-wider mb-2" data-id="element-846">
                        Year
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-847">2025</p>
                    </div>
                  </div>

                  <h2 className="text-3xl font-light mb-8 text-[#003071]" data-id="element-848">
                    Overview
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-849">
                    Case Western Reserve University hired me for an initially{' '}
                    <span className="font-semibold text-slate-900" data-id="element-850">
                      3-month contract to redesign their Faculty Information
                      System. The engagement extended to six months
                    </span>{' '}
                    as we tackled multiple parallel workstreams:{' '}
                    <span className="font-semibold text-slate-900" data-id="element-851">
                      navigation redesign, design system customization,
                      establishing UX best practices for developer workflows,
                      and exploring AI solutions
                    </span>{' '}
                    to streamline data entry.
                  </p>

                  <div className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-12" data-id="element-852">
                    <h3 className="text-xl font-medium mb-8 text-[#003071]" data-id="element-853">
                      Key Achievements
                    </h3>
                    <div className="space-y-4 mb-12" data-id="element-854">
                      {['Built faculty ownership through <span class="font-semibold text-slate-900">16 bi-weekly interviews across colleges</span>, fostering inclusive co-design that elevated stakeholder buy-in', 'Developed <span class="font-semibold text-slate-900">AI-powered proof of concept achieving 85%+ accuracy</span> in CV parsing', 'Secured <span class="font-semibold text-slate-900">two-month pilot program with Amazon</span> for real-world testing', 'Delivered three core improvements: <span class="font-semibold text-slate-900">standardized terminology, enhanced visual hierarchy, improved navigation</span>'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-855">
                          <CheckCircle className="w-5 h-5 text-[#003071] mt-1 flex-shrink-0" data-id="element-856" />
                          <span className="text-slate-700 text-lg" dangerouslySetInnerHTML={{
                        __html: item
                      }} data-id="element-857" />
                        </div>)}
                    </div>

                    <div data-id="element-858">
                      <LightboxImage src="https://cdn.magicpatterns.com/uploads/b1CFgyJEco99E9Ys6agrnZ/Home_Page_FIS.png" alt="Faculty Information System home page interface" label="FIS Home Page" data-id="element-859" />
                    </div>
                  </div>
                </section>

                {/* Objectives Section */}
                <section id="objectives" className="scroll-mt-24 sm:scroll-mt-32" data-id="element-860">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12 text-[#003071]" data-id="element-861">
                    Objectives
                  </h2>
                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-862">
                    Transform faculty productivity while establishing a scalable
                    foundation for AI-driven innovation across the university.
                  </p>

                  <div className="space-y-3 sm:space-y-4 mb-24 sm:mb-32" data-id="element-863">
                    {[{
                    title: 'Faculty Productivity',
                    desc: 'Reduce annual review time from 3+ hours to minutes through intuitive workflows'
                  }, {
                    title: 'Institutional Efficiency',
                    desc: 'Create a unified research ecosystem eliminating fragmented data silos'
                  }, {
                    title: 'Future Innovation',
                    desc: 'Establish scalable foundation for AI-powered research tools'
                  }, {
                    title: 'User Adoption',
                    desc: 'Transform skepticism into ownership through collaborative design'
                  }, {
                    title: 'Enterprise Scalability',
                    desc: 'Balance rapid execution with long-term maintainability in a live production environment'
                  }].map((card, idx) => <div key={idx} className="border border-gray-200 p-4 sm:p-6 rounded-lg hover:border-gray-300 transition-colors" data-id="element-864">
                        <div className="flex items-start gap-4 sm:gap-5" data-id="element-865">
                          <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-[#003071] flex items-center justify-center text-xs font-bold text-[#003071]" data-id="element-866">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-867">
                            <h3 className="text-base sm:text-lg font-medium text-[#003071] mb-1 sm:mb-2" data-id="element-868">
                              {card.title}
                            </h3>
                            <p className="text-sm sm:text-base text-slate-600 leading-relaxed" data-id="element-869">
                              {card.desc}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </section>

                {/* Discovery Section */}
                <section id="discovery" className="scroll-mt-24 sm:scroll-mt-32" data-id="element-870">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12 text-[#003071]" data-id="element-871">
                    Problem & Discovery
                  </h2>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-872">
                    The Faculty Information System serves as the university's
                    central platform for faculty profiles and activity tracking.{' '}
                    <span className="font-semibold text-slate-900" data-id="element-873">
                      Each year, faculty spend 3+ hours on annual reviews
                      requiring extensive manual data entry.
                    </span>{' '}
                    This hidden institutional tax had persisted through{' '}
                    <span className="font-semibold text-slate-900" data-id="element-874">
                      seven years of evolution without dedicated UX expertise.
                    </span>
                  </p>

                  <div className="mb-16 sm:mb-24 border-l-4 border-[#003071] pl-8 sm:pl-12 py-4" data-id="element-875">
                    <h3 className="text-sm font-bold text-[#003071] uppercase tracking-wider mb-4 sm:mb-6" data-id="element-876">
                      Problem Statement
                    </h3>
                    <p className="text-xl sm:text-2xl font-light leading-tight text-slate-900" data-id="element-877">
                      Through initial user interviews, we discovered that
                      technical complexity had become a barrier to entry.
                      Faculty were experiencing{' '}
                      <span className="font-semibold text-slate-900" data-id="element-878">
                        decision paralysis
                      </span>{' '}
                      from inconsistent terminology, poor information
                      architecture, and cluttered interfaces.
                    </p>
                  </div>

                  {/* Grouped Discovery Content */}
                  <div className="bg-gray-50 rounded-2xl p-8 sm:p-12 mb-16 sm:mb-24" data-id="element-879">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16" data-id="element-880">
                      {[{
                      icon: ChatCircle,
                      title: 'Terminology Confusion',
                      desc: 'Inconsistent language across schools created cognitive barriers'
                    }, {
                      icon: NavigationArrow,
                      title: 'Navigation Issues',
                      desc: 'Poor information architecture made finding information difficult'
                    }, {
                      icon: Stack,
                      title: 'Visual Hierarchy Problems',
                      desc: 'Cluttered pages lacking structure increased cognitive load'
                    }, {
                      icon: Clock,
                      title: 'Manual Data Entry Tax',
                      desc: 'Excessive manual workflows created institutional inefficiency'
                    }].map((item, idx) => <div key={idx} className="bg-white p-4 sm:p-5 rounded-xl border border-gray-100 shadow-sm" data-id="element-881">
                          <div className="flex items-start gap-3 sm:gap-4" data-id="element-882">
                            <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#003071] flex items-center justify-center text-white" data-id="element-883">
                              <item.icon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-884" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-885">
                              <h4 className="font-medium text-base sm:text-lg mb-1 sm:mb-2 text-[#003071]" data-id="element-886">
                                {item.title}
                              </h4>
                              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed" data-id="element-887">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>

                    <div data-id="element-888">
                      <LightboxImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/vRuKFVtSu1k26mpfBtXnY7/Before.png" alt="Legacy Faculty Information System interface" label="Before Redesign" data-id="element-889" />
                    </div>
                  </div>
                </section>

                {/* Process Section */}
                <section id="process" className="scroll-mt-24 sm:scroll-mt-32" data-id="element-890">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12 text-[#003071]" data-id="element-891">
                    Solution & Methodology
                  </h2>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-892">
                    I architected a{' '}
                    <span className="font-semibold text-slate-900" data-id="element-893">
                      dual-track strategy
                    </span>
                    : prioritizing immediate UX improvements while developing an
                    AI proof of concept in parallel. This approach delivered
                    value quickly while exploring transformative innovation.
                  </p>

                  {/* Methodology Framework */}
                  <div className="mb-24 sm:mb-32" data-id="element-894">
                    <h3 className="text-lg sm:text-xl font-medium mb-8 sm:mb-12 text-[#003071]" data-id="element-895">
                      Strategic Approach
                    </h3>
                    <VerticalTimeline data-id="element-896" />
                  </div>

                  <div className="space-y-24 sm:space-y-32 md:space-y-40" data-id="element-897">
                    {/* Step 1 */}
                    <div data-id="element-898">
                      <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-899">
                        <span className="text-4xl sm:text-5xl font-light text-slate-300" data-id="element-900">
                          01
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-medium text-[#003071]" data-id="element-901">
                          Strategic Research & Discovery
                        </h3>
                      </div>

                      <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8 max-w-3xl" data-id="element-902">
                        <span className="font-semibold text-slate-900" data-id="element-903">
                          With only 3 months initially budgeted, I made a
                          strategic decision:
                        </span>{' '}
                        skip traditional lengthy research phases and dive
                        immediately into user interviews.{' '}
                        <span className="font-semibold text-slate-900" data-id="element-904">
                          I designed a two-week sprint cycle with our PM,
                        </span>{' '}
                        testing with new faculty faces each iteration,
                        maximizing diverse input across{' '}
                        <span className="font-semibold text-slate-900" data-id="element-905">
                          7 colleges
                        </span>{' '}
                        while maintaining velocity.
                      </p>

                      <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 sm:mb-12 max-w-3xl" data-id="element-906">
                        Quick competitive research revealed most universities
                        relied on costly third-party services.{' '}
                        <span className="font-semibold text-slate-900" data-id="element-907">
                          I saw an opportunity: build an on-premise solution
                          they could white-label, creating valuable IP rather
                          than renting software.
                        </span>
                      </p>

                      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-908">
                        <div style={{
                        padding: '56.25% 0 0 0',
                        position: 'relative'
                      }} data-id="element-909">
                          <iframe src="https://player.vimeo.com/video/1142194486?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }} title="User interviews" data-id="element-910" />
                        </div>
                        <p className="text-sm text-gray-500 p-4 sm:p-6 text-center bg-gray-50 border-t border-gray-100" data-id="element-911">
                          Strategic user interviews capturing product sentiment
                          and pain points
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div data-id="element-912">
                      <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-913">
                        <span className="text-4xl sm:text-5xl font-light text-slate-300" data-id="element-914">
                          02
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-medium text-[#003071]" data-id="element-915">
                          Rapid Prototyping & Technical Translation
                        </h3>
                      </div>

                      <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8 max-w-3xl" data-id="element-916">
                        <span className="font-semibold text-slate-900" data-id="element-917">
                          I applied an AI-era approach to innovation that
                          replaces linear design processes with exponential
                          concept development.
                        </span>{' '}
                        <span className="font-semibold text-slate-900" data-id="element-918">
                          I used AI tools to rapidly generate and test multiple
                          design hypotheses simultaneously, achieving in weeks
                          what would traditionally take months.
                        </span>
                      </p>

                      <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 sm:mb-12 max-w-3xl" data-id="element-919">
                        <span className="font-semibold text-slate-900" data-id="element-920">
                          I conducted live prototyping workshops where I helped
                          faculty visualize their ideas in real-time,
                        </span>{' '}
                        saving each version and using AI to synthesize them into
                        a mean design. This AI-assisted approach found the
                        statistical "mean" that satisfied diverse stakeholder
                        needs, achieving{' '}
                        <span className="font-semibold text-slate-900" data-id="element-921">
                          UI consensus in 10 weeks.
                        </span>
                      </p>

                      {/* Grouped Process Content */}
                      <div className="bg-gray-50 rounded-2xl p-8 sm:p-12" data-id="element-922">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12" data-id="element-923">
                          <LightboxImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/5KpMMuX7A152SY5E7yEEkP/Ideation_1.png" alt="Navigation iteration 1" label="Iteration 1" data-id="element-924" />
                          <LightboxImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/fZ3J5Y9Kes9FYqDKGquNRG/Ideation_2.png" alt="Navigation iteration 2" label="Iteration 2" data-id="element-925" />
                          <LightboxImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/sAd42ZLH2mUmRLh4anJCPC/Ideation_3.png" alt="Navigation iteration 3" label="Iteration 3" data-id="element-926" />
                        </div>

                        <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-927">
                          <div style={{
                          padding: '56.25% 0 0 0',
                          position: 'relative'
                        }} data-id="element-928">
                            <iframe src="https://player.vimeo.com/video/1142229645?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%'
                          }} title="Collaboration sessions" data-id="element-929" />
                          </div>
                          <p className="text-sm text-gray-500 p-4 sm:p-6 text-center bg-gray-50 border-t border-gray-100" data-id="element-930">
                            Live collaborative design sessions with engineers
                            and stakeholders
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div data-id="element-931">
                      <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-932">
                        <span className="text-4xl sm:text-5xl font-light text-slate-300" data-id="element-933">
                          03
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-medium text-[#003071]" data-id="element-934">
                          AI Innovation & Strategic Partnerships
                        </h3>
                      </div>

                      <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8 max-w-3xl" data-id="element-935">
                        As both designer and inventor, I saw an opportunity to
                        create IP rather than rent software.{' '}
                        <span className="font-semibold text-slate-900" data-id="element-936">
                          Drawing on my Purdue AI program coursework, I dove
                          into the codebase to architect a custom tagging and
                          labeling system
                        </span>{' '}
                        for the FIS's complex infrastructure.
                      </p>

                      <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 sm:mb-12 max-w-3xl" data-id="element-937">
                        <span className="font-semibold text-slate-900" data-id="element-938">
                          I created a unified tagging taxonomy that mapped three
                          disparate systems into a single data flow.
                        </span>{' '}
                        I handed Python engineers working code snippets that
                        connected the dots on our data strategy.{' '}
                        <span className="font-semibold text-slate-900" data-id="element-939">
                          The proof of concept worked: a 36-page CV parsed in
                          seconds,
                        </span>{' '}
                        extracting information into our desired format,
                        presenting it for user editing, and populating backend
                        data tables.
                      </p>

                      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-940">
                        <div style={{
                        padding: '52.31% 0 0 0',
                        position: 'relative'
                      }} data-id="element-941">
                          <iframe src="https://player.vimeo.com/video/1148647481?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }} title="Prototype demo" data-id="element-942" />
                        </div>
                        <p className="text-sm text-gray-500 p-4 sm:p-6 text-center bg-gray-50 border-t border-gray-100" data-id="element-943">
                          First working prototype demonstrating real-time
                          AI-powered CV parsing
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Results Section */}
                <section id="results" className="scroll-mt-24 sm:scroll-mt-32" data-id="element-944">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12 text-[#003071]" data-id="element-945">
                    Business Impact & Outcomes
                  </h2>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-946">
                    The redesigned Faculty Information System launched to
                    positive feedback from both users and stakeholders. What
                    started as a fragmented system became a model of
                    human-centered design that established the foundation for
                    AI-driven innovation across the university. We delivered
                    three core improvements:{' '}
                    <span className="font-semibold text-slate-900" data-id="element-947">
                      standardized language that eliminated cross-departmental
                      confusion,
                    </span>{' '}
                    <span className="font-semibold text-slate-900" data-id="element-948">
                      enhanced information architecture that reduced cognitive
                      load through strategic content hierarchy,
                    </span>{' '}
                    and{' '}
                    <span className="font-semibold text-slate-900" data-id="element-949">
                      intuitive navigation that aligned system structure with
                      user mental models.
                    </span>
                  </p>

                  <div className="mb-16 sm:mb-24" data-id="element-950">
                    <ImageCarousel images={[{
                    src: 'https://cdn.magicpatterns.com/uploads/irjGEK8QTQH2hohHTgFZ4f/CWRU_COMPV.png',
                    alt: 'Interface improvements overview',
                    label: 'Interface Improvements'
                  }, {
                    src: 'https://cdn.magicpatterns.com/uploads/b1CFgyJEco99E9Ys6agrnZ/Home_Page_FIS.png',
                    alt: 'FIS home page',
                    label: 'FIS Home Page'
                  }, {
                    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vRuKFVtSu1k26mpfBtXnY7/Before.png',
                    alt: 'Before redesign',
                    label: 'Before Redesign'
                  }]} data-id="element-951" />
                  </div>

                  <div className="bg-[#003071] text-white p-8 sm:p-12 rounded-xl sm:rounded-2xl relative mb-16 sm:mb-24" data-id="element-952">
                    <div className="flex items-center gap-4 mb-8 sm:mb-12" data-id="element-953">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/10 rounded-xl flex items-center justify-center" data-id="element-954">
                        <Lightning className="w-6 h-6 sm:w-7 sm:h-7 text-white" data-id="element-955" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-light" data-id="element-956">
                        AI-Powered Innovation
                      </h3>
                    </div>

                    <div className="mb-8 sm:mb-12" data-id="element-957">
                      <p className="text-base sm:text-lg leading-relaxed text-white/90 mb-6 sm:mb-8 max-w-3xl" data-id="element-958">
                        Collaborating with Python engineers, I helped architect
                        a proof of concept that leveraged AI to automatically
                        parse professors' CVs and populate annual review data,
                        translating complex technical requirements into
                        intuitive automation.
                      </p>
                      <p className="text-base sm:text-lg leading-relaxed text-white/90 max-w-3xl" data-id="element-959">
                        We engineered a "Data Slingshot," a custom taxonomy and
                        field-mapping strategy with strict logic "buckets" for
                        extracted data. This approach enabled accurate parsing
                        of complex publication lists, transforming hours of
                        manual entry into seconds of automated intelligence.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-960">
                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 relative group flex flex-col justify-between h-full" data-id="element-961">
                        <div className="flex items-start justify-between mb-4" data-id="element-962">
                          <span className="block text-3xl sm:text-4xl font-bold" data-id="element-963">
                            85%+
                          </span>
                          <div className="relative" data-id="element-964">
                            <Info className="w-5 h-5 sm:w-6 sm:h-6 text-white/60 cursor-help" data-id="element-965" />
                            <div className="absolute bottom-full right-0 mb-2 w-64 sm:w-72 bg-white text-slate-700 p-4 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 text-sm leading-relaxed pointer-events-none z-10" data-id="element-966">
                              Accuracy rate for automatically parsing
                              publications from faculty CVs and mapping them to
                              specific FIS form fields, eliminating manual data
                              entry for this section.
                              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white" data-id="element-967"></div>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-968">
                          <h4 className="text-lg sm:text-xl font-bold mb-2" data-id="element-969">
                            Parsing Accuracy
                          </h4>
                          <p className="text-sm sm:text-base text-white/70 leading-relaxed" data-id="element-970">
                            Achieved in initial proof of concept
                          </p>
                        </div>
                      </div>

                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col justify-between h-full" data-id="element-971">
                        <div className="mb-4" data-id="element-972">
                          <span className="block text-3xl sm:text-4xl font-bold" data-id="element-973">
                            Seconds
                          </span>
                        </div>
                        <div data-id="element-974">
                          <h4 className="text-lg sm:text-xl font-bold mb-2" data-id="element-975">
                            Processing Time
                          </h4>
                          <p className="text-sm sm:text-base text-white/70 leading-relaxed" data-id="element-976">
                            Reduced from 30+ minutes per CV
                          </p>
                        </div>
                      </div>

                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col justify-between h-full" data-id="element-977">
                        <div className="mb-4" data-id="element-978">
                          <Rocket className="w-8 h-8 sm:w-10 sm:h-10 text-white/80" data-id="element-979" />
                        </div>
                        <div data-id="element-980">
                          <h4 className="text-lg sm:text-xl font-bold mb-2" data-id="element-981">
                            Amazon Pilot Program
                          </h4>
                          <p className="text-sm sm:text-base text-white/70 leading-relaxed" data-id="element-982">
                            Secured two-month pilot for real-world testing
                          </p>
                        </div>
                      </div>

                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col justify-between h-full" data-id="element-983">
                        <div className="mb-4" data-id="element-984">
                          <Target className="w-8 h-8 sm:w-10 sm:h-10 text-white/80" data-id="element-985" />
                        </div>
                        <div data-id="element-986">
                          <h4 className="text-lg sm:text-xl font-bold mb-2" data-id="element-987">
                            2026 Implementation
                          </h4>
                          <p className="text-sm sm:text-base text-white/70 leading-relaxed" data-id="element-988">
                            Full implementation scheduled for 2026
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-989">
                    Before my departure, stakeholders highlighted these
                    achievements in university presentations as the system
                    continues its phased rollout.{' '}
                    <span className="font-semibold text-slate-900" data-id="element-990">
                      Faculty and staff expressed their appreciation for my push
                      for innovation and user-centered design.
                    </span>{' '}
                    Understanding budget constraints prevented hiring a designer
                    post-departure,{' '}
                    <span className="font-semibold text-slate-900" data-id="element-991">
                      I trained two team members on UX and UI design best
                      practices,
                    </span>{' '}
                    ensuring the project's continued success and empowering the
                    team to maintain design quality as the system scales.
                  </p>

                  <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-8 sm:p-12 border border-gray-100" data-id="element-992">
                    <h3 className="text-xl sm:text-2xl font-medium mb-6 sm:mb-8 text-[#003071]" data-id="element-993">
                      Key Learnings
                    </h3>
                    <div className="space-y-6 sm:space-y-8" data-id="element-994">
                      <div data-id="element-995">
                        <h4 className="font-semibold text-slate-900 mb-2 sm:mb-3 text-base sm:text-lg" data-id="element-996">
                          Strategic Time Constraints Drive Innovation
                        </h4>
                        <p className="text-slate-600 leading-relaxed text-base sm:text-lg" data-id="element-997">
                          The 3-month timeline forced me to abandon traditional
                          research phases and embrace the Stingray methodology.
                          This constraint became an advantage, enabling rapid
                          iteration and AI-assisted design synthesis.
                        </p>
                      </div>
                      <div data-id="element-998">
                        <h4 className="font-semibold text-slate-900 mb-2 sm:mb-3 text-base sm:text-lg" data-id="element-999">
                          Technical Skills Amplify Design Impact
                        </h4>
                        <p className="text-slate-600 leading-relaxed text-base sm:text-lg" data-id="element-1000">
                          My Purdue AI program training enabled me to bridge
                          design and engineering, delivering working Python code
                          alongside design specs. This technical fluency
                          accelerated development and proved concepts before
                          significant engineering investment.
                        </p>
                      </div>
                      <div data-id="element-1001">
                        <h4 className="font-semibold text-slate-900 mb-2 sm:mb-3 text-base sm:text-lg" data-id="element-1002">
                          Knowledge Transfer Ensures Longevity
                        </h4>
                        <p className="text-slate-600 leading-relaxed text-base sm:text-lg" data-id="element-1003">
                          Training team members on UX best practices proved as
                          valuable as the designs themselves. Empowering the
                          team to maintain design quality post-departure ensured
                          the project's continued success and scalability.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {!isOverlay && <CaseStudyNavigation homePath={homePath} nextCase={{
          category: 'Case Studies',
          title: 'U.S. Bank',
          slug: 'us-bank'
        }} data-id="element-1004" />}

          {!isOverlay && <Footer data-id="element-1005" />}
        </div>

        {!isOverlay && <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-1006" />}
      </div>

      {!isOverlay && !isChatDrawerOpen && <FloatingButtons data-id="element-1007" />}
    </>;
}